import { FC, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderProfileActionEnum, ServicingAccessPoint, ProviderHubTinModel, ProviderHubFacilityModel, ContractEntityProvidersFiltersRequestModel, ContractEntityProvidersFilters, FiltersAction, ProviderHubContractEntityProviders, ProviderHubContractEntityCcnGroup, GapImpactPaginationResult } from "@app/projects/network-assessment/provider-hub/interfaces";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { useLocation } from "react-router-dom";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { NewActionMenu } from "@app/shared/action-menu";
import { PaginationResult, WafflesPaginationProps } from "@app/shared/waffles/exports";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import ContractEntityProfileActionFlyout from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityProfileActionFlyout";
import { ContractEntityProfileKeyPoints } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityProfileKeyPoints";
import { ContractEntityProfileOverview } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityProfileOverview";
import { ContractEntityProfileProviders } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityProfileProviders";
import ContractEntityProfileTable from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityProfileTable";
import ContractEntityFacilitiesTable from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityFacilitiesTable";
import ContractEntityEmployedPractitionersTable from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityEmployedPractitionersTable";
import ProfileInformationPanel from "@app/projects/network-assessment/provider-hub/ProfileInformationPanel";
import { NestingParentDropdownItem, DropdownItem } from "@app/shared/checkbox-dropdown/types";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { ContractEntityFilters } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ContractEntityFilters";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ProviderProfileRouteState } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileRouteState";
import { ProviderHubSpecialty } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ProviderHubSpecialtyView";
import { ContractEntityProfileCareJourney } from "@app/projects/network-assessment/provider-hub/contract-entity-profile/ProviderHubCareJourneyView";
import { UserContext } from "@app/user/UserContext";
import { showLegacyQuinnButtons } from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";


// Views that group/paginate client-side (Employed Practitioners "by Organization", the flat NPI (Type 2)
// facility list, and each CCN group's children) fetch their full row set in one page rather than server-paginating,
// so the client-side grouping/pager sees every row. Sized well above any realistic contract entity's row count.
// (Interim until backend parent-paginated + lazy-children endpoints replace these client-side groupings.)
const MAX_CLIENT_PAGINATED_FETCH_SIZE = 500;

export const ProviderHubContractEntityProfile: FC = () => {
  const location = useLocation<ProviderProfileRouteState>();
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  // Hospital Profile off: no Employed Practitioners / Facilities split — collapse to the flat Providers view.
  const hospitalProfileEnabled = featureFlagContext.enabledFeatureFlags.has(Features.HOSPITAL_PROFILE);
  const { selectedContractEntity } = providerHubContext;
  const providerHubService = useRef(new ProviderHubService()).current;
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [accessPoints, setAccessPoints] = useState<ServicingAccessPoint[]>([]);
  const [isLoadingAccessPoints, setIsLoadingAccessPoints] = useState(true);
  const outOfNetworkTypes = useRef<ProviderNetworkTypeEnum[]>([ProviderNetworkTypeEnum.Prospect, ProviderNetworkTypeEnum.Market, ProviderNetworkTypeEnum.NetNew]).current;
  const [specialtyOptions, setSpecialtyOptions] = useState<DropdownItem<string>[]>([]);
  const [contractEntityProviderTableData, setContractEntityProviderTableData] = useState<PaginationResult<ProviderHubContractEntityProviders>>({ items: [], totalCount: 0 });
  // Facilities tab (NPI (Type 2) flat view): type-2 facilities for the CE.
  const [facilitiesData, setFacilitiesData] = useState<ProviderHubContractEntityProviders[]>([]);
  const [facilitiesUseGapsByRegionStatus, setFacilitiesUseGapsByRegionStatus] = useState<boolean>(false);
  // Facilities tab (CCN view, Option A parent-pagination): server-paginated CCN (hospital) parent rows,
  // plus a lazily-filled cache of each CCN's child facilities (keyed by the parent row key).
  const [facilityCcnGroups, setFacilityCcnGroups] = useState<GapImpactPaginationResult<ProviderHubContractEntityCcnGroup>>({ items: [], totalCount: 0 });
  const [facilityChildrenByCcn, setFacilityChildrenByCcn] = useState<Map<string, ProviderHubContractEntityProviders[]>>(new Map());
  const [facilityPageIndex, setFacilityPageIndex] = useState<number>(1);
  const [facilityPageSize, setFacilityPageSize] = useState<number>(50);
  const [facilityResetToFirstPage, setFacilityResetToFirstPage] = useState<boolean>(false);
  // While a table's data is being fetched, show the Waffles spinner instead of the empty-table message.
  // The default views (Organization + CCN) load on mount, so they start in the loading state.
  const [employedPractitionersLoading, setEmployedPractitionersLoading] = useState<boolean>(true);
  const [facilityCcnGroupsLoading, setFacilityCcnGroupsLoading] = useState<boolean>(true);
  const [facilitiesLoading, setFacilitiesLoading] = useState<boolean>(false);
  const [providersLoading, setProvidersLoading] = useState<boolean>(false);
  // Employed Practitioners tab: type-1 practitioners for the CE, grouped by organization client-side.
  const [employedPractitionersData, setEmployedPractitionersData] = useState<ProviderHubContractEntityProviders[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [isFilterReset, setIsFilterReset] = useState<boolean>(false);
  // split the CE provider panel into Employed Practitioners (type-1) and Facilities (type-2) tabs.
  // Walking skeleton: the existing table lives under Employed Practitioners; Facilities is a stub for now.
  const [activeProfileTab, setActiveProfileTab] = useState<"practitioners" | "facilities">("practitioners");
  // Employed Practitioners tab: View by Organization (default, expandable) vs. Providers (flat).
  const [practitionersView, setPractitionersView] = useState<"organization" | "providers">(hospitalProfileEnabled ? "organization" : "providers");
  // Facilities tab: View by CCN (default, expandable) vs. NPI (Type 2) (flat, with a CCN column).
  const [facilitiesView, setFacilitiesView] = useState<"ccn" | "npi2">("ccn");

  const userContext = useContext(UserContext);
  const { activeServiceAreaStatusFilters, serviceAreaMatchFilter, serviceAreaOptions, serviceAreaStatusFilters, setActiveServiceAreaStatusFilters, setServiceAreaOptions } = providerHubContext;
  /* oxlint-disable sort-keys */
  const providerNetworkTypeOptions: NestingParentDropdownItem[] = [
    { key: ProviderNetworkTypeEnum.Network, name: 'In-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [] },
    {
      key: 'Out-of-Network', name: 'Out-of-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [
        { key: ProviderNetworkTypeEnum.Prospect, name: 'Prospect' },
        { key: ProviderNetworkTypeEnum.Market, name: 'Market' },
        { key: ProviderNetworkTypeEnum.NetNew, name: 'Net New' }
      ]
    }
  ];

  const accessPointsForTermination = useMemo(() => {
    return accessPoints
      .filter(ap => [ProviderNetworkTypeEnum.Network].includes(ap.providerNetworkType) && !([ProviderActionStatusEnum.FullyTerminated].includes(ap.providerActionStatus)));
  }, [accessPoints]);

  const networkAccessPoints = useMemo(() => {
    return accessPoints.filter(ap => [ProviderNetworkTypeEnum.Network].includes(ap.providerNetworkType));
  }, [accessPoints]);

  const outOfNetworkAccessPoints = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType));
  }, [accessPoints, outOfNetworkTypes]);

  const accessPointsForTargeting = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)
      && !([ProviderActionStatusEnum.FullyTargeted].includes(ap.providerActionStatus)));
  }, [accessPoints, outOfNetworkTypes]);

  const accessPointsForExclusion = useMemo(() => {
    return accessPoints.filter(ap => outOfNetworkTypes.includes(ap.providerNetworkType)
      && !([ProviderActionStatusEnum.FullyExcluded].includes(ap.providerActionStatus)));
  }, [accessPoints, outOfNetworkTypes]);

  const ceIsExcluded = useMemo(() => {
    return outOfNetworkAccessPoints.length > 0 && outOfNetworkAccessPoints.every(ap => ap.providerActionStatus === ProviderActionStatusEnum.FullyExcluded);
  }, [outOfNetworkAccessPoints]);
  
  const hasAvailableActions = useMemo(() => {
    const hasExcludeRole = userContext.roles.includes(Roles.PROVIDER_EXCLUDE_USER);
    const hasTargetRole = userContext.roles.includes(Roles.PROVIDER_TARGET_USER);
    const hasTerminateRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);

    if (!hasExcludeRole && !hasTargetRole && !hasTerminateRole) {
      return false;
    }

    const hasExcludeAction = hasExcludeRole && outOfNetworkAccessPoints.length > 0;
    const hasTargetAction = hasTargetRole && outOfNetworkAccessPoints.length > 0;
    const hasTerminateAction = hasTerminateRole && networkAccessPoints.length > 0;

    return hasExcludeAction || hasTargetAction || hasTerminateAction;
  }, [outOfNetworkAccessPoints, networkAccessPoints, userContext.roles]);

  useEffect(() => {
    if (!!selectedContractEntity) {
      fetchAccessPoints();
    }
  }, [selectedContractEntity]);

  useEffect(() => {
    // location.pathname should be /products/:productId/provider-hub[/run/:runConfigId/provider-profile/:providerKey] (where [] is optional)
    // so keeping only the first three ("", "products", productId) and updating the module should be safe regardless of the extra parameters
    const productUrl = location.pathname.split("/").slice(0, 3).join("/") + "/adequacy";
    const middleCrumb: Breadcrumb = {
      onClick: returnToContractEntities,
      text: "Contract Entity",
    };

    projectContext.setBreadcrumbItems([
      {
        link: productUrl,
        text: projectContext.project.resource.name,
      },
      middleCrumb,
      {
        text: !selectedContractEntity
          ? providerHubContext.errorLoadingContractEntity
            ? "Error"
            : "Loading"
          : selectedContractEntity?.name,
      }
    ]);
  }, [projectContext.project.resource.name, providerHubContext.selectedContractEntity]);

  useEffect(() => {
    let page = providerHubContext.accessPointsPage;
    const totalPages = Math.ceil(accessPoints.length / providerHubContext.accessPointsPageSize);

    // If the current page is greater than the total pages, reset to page 1
    if (page > totalPages) {
      page = 1;
      providerHubContext.setAccessPointsPage(1);
    }

    providerHubContext.setTotalAccessPointsPages(totalPages);

    providerHubContext.setPagedAccessPoints(accessPoints.slice((page - 1) * providerHubContext.accessPointsPageSize, page * providerHubContext.accessPointsPageSize));

  }, [accessPoints, providerHubContext.accessPointsPage, providerHubContext.accessPointsPageSize]);

  const fetchAccessPoints = useCallback(() => {
    providerHubService
      .getAccessPointsByContractEntity(projectContext?.project?.projectId, selectedContractEntity.projectRunConfigurationId, selectedContractEntity.contractEntityId)
      .then((ap) => {
        setAccessPoints(ap);
        setIsLoadingAccessPoints(false);
      });
  }, [projectContext?.project?.projectId, selectedContractEntity, providerHubService]);

  // Go Back is now browser Back (the URL is the source of truth for which profile shows); this wrapper just
  // clears the CE-profile error state and resets the breadcrumb before popping the history entry.
  const returnToContractEntities = () => {
    providerHubContext.setErrorLoadingContractEntity(false);
    projectContext.setBreadcrumbItems([projectContext.project.resource.name]);
    providerHubContext.goBackFromProfile();
  };

  useEffect(() => {
    // Check if context already has regionSetIds set (user came from another view)
    if (providerHubContext.filterState.regionSetIds && providerHubContext.filterState.regionSetIds.length > 0) {
      setFilter('regionSetIds', providerHubContext.filterState.regionSetIds);
    }
    if (providerHubContext.filterState.providerNetworkTypes && providerHubContext.filterState.providerNetworkTypes.length > 0) {
      setFilter('providerNetworkTypes', providerHubContext.filterState.providerNetworkTypes);
      setSelectedProviderNetworkTypes(providerHubContext.filterState.providerNetworkTypes);
    }
    if (providerHubContext.filterState.specialtyGroupCodes && providerHubContext.filterState.specialtyGroupCodes.length > 0) {
      setFilter('specialtyGroupCodes', providerHubContext.filterState.specialtyGroupCodes);
    }
  }, []);

  const initialFilterState: ContractEntityProvidersFilters = {
    pageIndex: 0,
    pageSize: 50,
    providerNetworkTypes: providerHubContext.filterState.providerNetworkTypes ?? [],
    regionSetIds: providerHubContext.filterState.regionSetIds ?? [],
    specialtyGroupCodes: providerHubContext.filterState.specialtyGroupCodes ?? [],
  }

  const filtersReducer = (filtersState: ContractEntityProvidersFilters, action: FiltersAction): ContractEntityProvidersFilters => {
    switch (action.type) {
      case 'SET_FILTER':
        return {
          ...filtersState,
          [action.key]: action.value,
        };
      case 'RESET_FILTERS':
        return initialFilterState;
      default:
        return initialFilterState;
    }
  };

  const [filterState, filterStateDispatch] = useReducer(filtersReducer, initialFilterState);
  const [selectedProviderNetworkTypes, setSelectedProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(filterState.providerNetworkTypes);

  const handleSearchClick = async () => {
    await refreshData(true);
    refreshGroupedTabData(true);
  }


  // Facilities tab: a facility's CCN links to the Hospital profile in the same tab (mirrors the
  // Organization-name -> Organization profile drill-down). Referrer is ContractEntityProfile so "go back" returns here.
  const handleCcnClick = async (ccn: string) => {
    const hospital = await providerHubService.getHospitalDetails(projectContext?.project?.projectId, selectedContractEntity.projectRunConfigurationId, ccn);
    providerHubContext.navigateToHospitalProfile(hospital, ProviderProfileReferrerEnum.ContractEntityProfile);
  }

  // Organization profile in the same tab — from an Employed Practitioners organization group, or a Facilities row's
  // own type-2 NPI.
  const handleOrganizationClick = async (organizationNpi: string, contractEntityId?: number) => {
    const organization = await providerHubService.getOrganizationDetails(projectContext?.project?.projectId, selectedContractEntity.projectRunConfigurationId, organizationNpi, contractEntityId ?? selectedContractEntity.contractEntityId);
    providerHubContext.navigateToOrganizationProfile(organization, selectedContractEntity, ProviderProfileReferrerEnum.ContractEntityProfile);
  }

  // Employed Practitioners tab: a practitioner (child row) links to the Provider profile in the same tab.
  const handlePractitionerClick = async (practitioner: ProviderHubContractEntityProviders) => {
    const provider = await providerHubService.getServicingProviderDetails(projectContext?.project?.projectId, selectedContractEntity.projectRunConfigurationId, practitioner.servicingProviderKey);
    providerHubContext.navigateToProviderProfile(provider, ProviderProfileReferrerEnum.ContractEntityProfile);
  }

  useEffect(() => {
    if(isFilterReset) {
      refreshData(true);
      refreshGroupedTabData(true);
      setIsFilterReset(false);
    }
  }, [isFilterReset]);

  // (re)load the CE's grouped-tab data — type-2 facilities (grouped by CCN) and type-1 practitioners
  // (grouped by organization), both client-side. Runs on CE change; re-runs on Search / Reset via the same
  // filterState the flat provider table uses, so the filter bar applies to these tabs too.
  useEffect(() => {
    refreshGroupedTabData(true);
  }, [providerHubContext.selectedContractEntity]);

  // Build the filter payload shared by the flat provider/organization tables and the grouped (Facilities /
  // Employed Practitioners) tabs, so a single filter bar drives all of them consistently.
  const buildFilterRequestModel = (): ContractEntityProvidersFiltersRequestModel => ({
    pageIndex: pageIndex,
    pageSize: pageSize,
    providerNetworkTypes: filterState.providerNetworkTypes,
    regionSetIds: filterState.regionSetIds.map(Number),
    specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
  });

  // re-fetch the practitioners grouped tab (client-grouping interim) with the current filters.
  // Employed Practitioners tab ("by Organization" view): fetch the CE's full type-1 practitioner list in one page;
  // the table groups by organization and paginates client-side. Fetch all so no organizations are silently dropped
  // by the default page size. (Interim until a backend parent-paginated + lazy-children endpoint replaces this.)
  const refreshPractitioners = () => {
    if (!providerHubContext.selectedContractEntity) return;
    const { projectRunConfigurationId, contractEntityId } = providerHubContext.selectedContractEntity;
    setEmployedPractitionersLoading(true);
    providerHubService.getContractEntityEmployedPractitioners(
      1, MAX_CLIENT_PAGINATED_FETCH_SIZE, projectContext?.project?.projectId, projectRunConfigurationId, contractEntityId,
      { ...buildFilterRequestModel(), pageIndex: 1, pageSize: MAX_CLIENT_PAGINATED_FETCH_SIZE }
    ).then((data) => setEmployedPractitionersData(data.items))
      .finally(() => setEmployedPractitionersLoading(false));
  };

  // Facilities tab (NPI (Type 2) flat view): fetch the flat facility list. Only needed while that view is
  // active — the default CCN view uses parent-pagination + lazy children instead.
  // Facilities tab (flat NPI (Type 2) view): fetch the CE's full facility list in one page; the table paginates
  // it client-side. Fetch all so the client-side pager isn't silently capped at the default page size.
  const refreshFlatFacilities = () => {
    if (!providerHubContext.selectedContractEntity) return;
    const { projectRunConfigurationId, contractEntityId } = providerHubContext.selectedContractEntity;
    setFacilitiesLoading(true);
    providerHubService.getContractEntityFacilities(
      1, MAX_CLIENT_PAGINATED_FETCH_SIZE, projectContext?.project?.projectId, projectRunConfigurationId, contractEntityId,
      { ...buildFilterRequestModel(), pageIndex: 1, pageSize: MAX_CLIENT_PAGINATED_FETCH_SIZE }
    ).then((data) => {
      setFacilitiesData(data.items);
      setFacilitiesUseGapsByRegionStatus(data.useGapsByRegionStatus === true);
    })
      .finally(() => setFacilitiesLoading(false));
  };

  // Facilities tab (CCN view, Option A parent-pagination): fetch the server-paginated CCN parent rows and
  // reset the lazy child cache (children are re-fetched on demand as groups are expanded).
  const refreshFacilityCcnGroups = (useFirstPage: boolean = false) => {
    if (!providerHubContext.selectedContractEntity) return;
    if (useFirstPage) {
      setFacilityPageIndex(1);
      setFacilityResetToFirstPage(true);
    }
    const { projectRunConfigurationId, contractEntityId } = providerHubContext.selectedContractEntity;
    setFacilityCcnGroupsLoading(true);
    providerHubService.getContractEntityFacilityCcnGroups(
      useFirstPage ? 1 : facilityPageIndex, facilityPageSize, projectContext?.project?.projectId, projectRunConfigurationId, contractEntityId, buildFilterRequestModel()
    ).then((data) => {
      setFacilityCcnGroups(data);
      setFacilityChildrenByCcn(new Map());
    }).finally(() => setFacilityCcnGroupsLoading(false));
  };

  // Facilities tab (CCN view): lazily load one CCN group's child facilities on first expand. The parent key
  // is `ccn:<ccn>` for a hospital group or "other-facilities" for the no-CCN bucket.
  const handleExpandCcn = async (parentKey: string): Promise<void> => {
    if (!providerHubContext.selectedContractEntity || facilityChildrenByCcn.has(parentKey)) return;
    const isOtherFacilities = parentKey === "other-facilities";
    const ccn = isOtherFacilities ? undefined : parentKey.replace(/^ccn:/, "");
    const { projectRunConfigurationId, contractEntityId } = providerHubContext.selectedContractEntity;
    // One page large enough to hold all of a CCN's facilities — child rows are not independently paginated.
    const data = await providerHubService.getContractEntityFacilities(
      1, MAX_CLIENT_PAGINATED_FETCH_SIZE, projectContext?.project?.projectId, projectRunConfigurationId, contractEntityId,
      { ...buildFilterRequestModel(), pageIndex: 1, pageSize: MAX_CLIENT_PAGINATED_FETCH_SIZE, ccn, otherFacilitiesOnly: isOtherFacilities }
    );
    setFacilityChildrenByCcn((prev) => new Map(prev).set(parentKey, data.items));
  };

  // (re)load both grouped tabs with the current filters. Facilities defaults to the CCN parent-paginated
  // view; the flat NPI2 list is only fetched when that view is active.
  const refreshGroupedTabData = (useFirstPage: boolean = false) => {
    refreshPractitioners();
    refreshFacilityCcnGroups(useFirstPage);
    if (facilitiesView === "npi2") {
      refreshFlatFacilities();
    }
  };

  const facilityCcnPaginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => { setFacilityPageIndex(pageNumber); setFacilityResetToFirstPage(false); },
    enableBackendPagination: true,
    handlePageSizeChange: (newPageSize: number) => { setFacilityPageSize(newPageSize); setFacilityPageIndex(1); },
    pageSizeOptions: [50, 100, 150],
    recordsPerPage: facilityPageSize,
    resetToFirstPage: facilityResetToFirstPage,
    showPageSizeSelector: true,
    totalRecords: facilityCcnGroups.totalCount,
  };

  const refreshData = async (useFirstPage: boolean = false) => {
    const filterRequestModel: ContractEntityProvidersFiltersRequestModel = buildFilterRequestModel();

    if (useFirstPage) {
      setPageIndex(1);
      setResetToFirstPage(true);
    }
    if (providerHubContext.selectedContractEntity) {
      setProvidersLoading(true);
      providerHubService.getContractEntityProviderDetails(useFirstPage ? 1 : pageIndex, pageSize, projectContext?.project?.projectId,
        providerHubContext.selectedContractEntity.projectRunConfigurationId, providerHubContext.selectedContractEntity.contractEntityId, filterRequestModel)
        .then((data) => {
          setContractEntityProviderTableData(data);
        })
        .finally(() => setProvidersLoading(false));
    }
  }
  const setFilter = <T extends keyof ContractEntityProvidersFilters>(key: T, value: ContractEntityProvidersFilters[T]) => {
    filterStateDispatch({ key, type: 'SET_FILTER', value });
  };

  const handleResetAll = async () => {
  filterStateDispatch({ type: 'RESET_FILTERS' });
  setFilter('providerNetworkTypes', []);
  setSelectedProviderNetworkTypes([]);
  setFilter('specialtyGroupCodes', []);
  setFilter('regionSetIds', []);
  setActiveServiceAreaStatusFilters(new Set());

  // Update the ProviderHub context state properly using setFilter
  providerHubContext.setFilter('specialtyGroupCodes', []);
  providerHubContext.setFilter('providerNetworkTypes', []);
  providerHubContext.setFilter('regionSetIds', []);
  setIsFilterReset(true);
}

  useEffect(() => {
    refreshData();
  }, [pageIndex, pageSize, selectedContractEntity]);

  // Facilities tab (CCN view): re-fetch the CCN parent rows when the user pages the parent grid.
  useEffect(() => {
    refreshFacilityCcnGroups();
  }, [facilityPageIndex, facilityPageSize]);

  // Facilities tab: the flat NPI (Type 2) list is fetched only when that view is selected (the default CCN
  // view uses parent-pagination + lazy children instead).
  useEffect(() => {
    if (facilitiesView === "npi2") {
      refreshFlatFacilities();
    }
  }, [facilitiesView]);

  useEffect(() => {
    // The waffles-container is the scrolling element — its height is fixed and table rows scroll within it
    rightColumnRef.current?.querySelector('.waffles-container')?.scrollTo(0, 0);
  }, [pageIndex]);

  // the "View by" filter is a single-select dropdown inline with the other filters. Its options track the
  // active tab — Employed Practitioners groups by Organization/Providers; Facilities groups by CCN/NPI (Type 2).
  /* oxlint-disable sort-keys */
  const practitionerViewByOptions: DropdownItem<string>[] = hospitalProfileEnabled
    ? [
      { key: "organization", name: "Organization" },
      { key: "providers", name: "Providers" },
    ]
    : [
      { key: "providers", name: "Providers" },
    ];
  const facilitiesViewByOptions: DropdownItem<string>[] = [
    { key: "ccn", name: "CCN" },
    { key: "npi2", name: "NPI (Type 2)" },
  ];
  /* oxlint-enable sort-keys */
  const viewByOptions = activeProfileTab === "practitioners" ? practitionerViewByOptions : facilitiesViewByOptions;
  const selectedViewByKey = activeProfileTab === "practitioners" ? practitionersView : facilitiesView;
  const handleViewByChange = (key: string) => {
    if (activeProfileTab === "practitioners") {
      setPractitionersView(key as "organization" | "providers");
    } else {
      setFacilitiesView(key as "ccn" | "npi2");
    }
  };

  // Guard AFTER all hooks so hook order stays stable across loading/error/loaded renders (Rules of Hooks).
  if (!selectedContractEntity && !providerHubContext.errorLoadingContractEntity) {
    return <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="loading Contract Entity" />;
  } else if (providerHubContext.errorLoadingContractEntity) {
    return <QESAlert type="danger" persist={true}>We did not find the requested Contract Entity.</QESAlert>
  }

  return (
    <>
      <div className="contractentity-profile__grid contractentity-profile__grid--tabs">
        <div className="contractentity-profile__top-row">
          <div className="contractentity-profile__top-row-buttons">
            <QESButton
              qesType="outline-inherit"
              qesTestId="provider-hub-provider-detail__back-to-results"
              onClick={returnToContractEntities}
              className="provider-profile__back-to-results"
            >
              Go Back
            </QESButton>
            <div className="provider-profile__quinn-container">
              {showLegacyQuinnButtons(featureFlagContext.enabledFeatureFlags, userContext.roles) &&
                <QESButton
                  className="provider-profile__quinn"
                  onClick={() => projectContext.setVirtualAssistantContext({ isOpen: true })}
                  qesType="outline"
                  qesTestId="provider-hub__show-virtual-assistant"
                >
                  <QesIcon iconName={QesIcons.Quinn} description="Quinn" />
                  <div>
                    Ask Quinn
                  </div>
                </QESButton>
              }
            </div>
            {(!providerHubContext.isScenarioModelingProcessing &&
              !providerHubContext.isScenarioModelingError && 
              featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
              (hasAvailableActions || isLoadingAccessPoints)) &&
              <div className="provider-profile__provider-actions-info">
                <div>
                  <TooltipWrapper
                    text="Actions will affect all applicable contract entity access points"
                    position="bottom"
                  >
                    <QesIcon iconName={QesIcons.InfoCircle} description="Tooltip" />
                  </TooltipWrapper>
                </div>
                <NewActionMenu
                  alignRight={true}
                  buttonText="Contract Entity Actions"
                  containerClassnames="provider-profile__provider-actions-menu"
                  testId="provider-actions-select"
                  buttonClassNames="qes-button-primary"
                >
                  {outOfNetworkAccessPoints.length > 0 &&
                    <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
                      <FlyoutLink
                        content={{
                          body: accessPointsForExclusion.length > 0
                            ? <ContractEntityProfileActionFlyout
                              accessPoint={accessPointsForExclusion.find(a => a.providerNetworkType !== ProviderNetworkTypeEnum.NetNew) ?? accessPointsForExclusion[0]}
                              providerAction={ProviderProfileActionEnum.Exclude}
                              allAccessPoints={accessPointsForExclusion.length === outOfNetworkAccessPoints.length}
                              onSubmit={fetchAccessPoints}
                              targetedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForTargeting.length}
                            />
                            : <></>,
                          title: "Exclude Contract Entity"
                        }}
                        disabled={accessPointsForExclusion.length === 0}
                        testId="provider-hub-provider-profile__exclude-flyout"
                        classNames="action-menu-item col-12 fw-normal"
                        size="60"
                      >
                        {accessPointsForExclusion.length === 0 ? (
                          <TooltipWrapper text="The contract entity is fully excluded across all applicable access points" position="bottom">
                            Exclude
                          </TooltipWrapper>
                        ) : (
                          "Exclude"
                        )}
                      </FlyoutLink>
                    </RoleRestricted>
                  }

                  {outOfNetworkAccessPoints.length > 0 &&
                    <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
                      <FlyoutLink
                        content={{
                          body: accessPointsForTargeting.length > 0
                            ? <ContractEntityProfileActionFlyout
                              accessPoint={accessPointsForTargeting.find(a => a.providerNetworkType !== ProviderNetworkTypeEnum.NetNew) ?? accessPointsForTargeting[0]}
                              providerAction={ProviderProfileActionEnum.Target}
                              allAccessPoints={accessPointsForTargeting.length === outOfNetworkAccessPoints.length}
                              onSubmit={fetchAccessPoints}
                              excludedAccessPointsCount={outOfNetworkAccessPoints.length - accessPointsForExclusion.length}
                            />
                            : <></>,
                          title: "Target Contract Entity"
                        }}
                        disabled={accessPointsForTargeting.length === 0 || ceIsExcluded}
                        testId="provider-hub-provider-profile__target-flyout"
                        classNames="action-menu-item col-12 fw-normal"
                        size="40"
                      >
                        {accessPointsForTargeting.length === 0 ? (
                          <TooltipWrapper text="The contract entity is fully targeted across all applicable access points" position="bottom">
                            Target
                          </TooltipWrapper>
                        ) : ceIsExcluded ? (
                          <TooltipWrapper text="This contract entity is fully excluded across all applicable access points. To target, remove the Exclusion." position="bottom">
                            Target
                          </TooltipWrapper>
                        ) : (
                          "Target"
                        )}
                      </FlyoutLink>
                    </RoleRestricted>
                  }

                  {networkAccessPoints.length > 0 &&
                    <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                      <FlyoutLink
                        content={{
                          body: accessPointsForTermination.length > 0
                            ? <ContractEntityProfileActionFlyout
                              accessPoint={accessPointsForTermination[0]}
                              providerAction={ProviderProfileActionEnum.Terminate}
                              allAccessPoints={accessPoints.length === accessPointsForTermination.length}
                              onSubmit={fetchAccessPoints}
                            />
                            : <></>,
                          title: "Terminate Contract Entity"
                        }}
                        disabled={accessPointsForTermination.length === 0}
                        testId="provider-hub-provider-profile__terminate-flyout"
                        classNames="action-menu-item col-12 fw-normal"
                        size="40"
                      >
                        {accessPointsForTermination.length === 0 ? (
                          <TooltipWrapper text="The contract entity is fully terminated across all applicable access points" position="bottom">
                            Terminate
                          </TooltipWrapper>
                        ) : (
                          "Terminate"
                        )}
                      </FlyoutLink>
                    </RoleRestricted>
                  }
                </NewActionMenu>
              </div>
            }
          </div>
        </div>
        <div className="contractentity-profile__name-container">
          <h1 className="contractentity-profile__name">{selectedContractEntity.name}</h1>
          <TooltipWrapper
            text="Information in the left-hand accordions does not reflect any applied filters"
            position="bottom"
          >
            <QesIcon className="contractentity-profile__name-icon" iconName={QesIcons.InfoCircle} description="Tooltip" />
          </TooltipWrapper>
        </div>
        {/* Tab switcher: right side of the profile, in line with the name, directly above the filters row. */}
        {/* Hidden when Hospital Profile is off — there is only the flat Providers view, so no tabs to switch. */}
        {hospitalProfileEnabled && (
          <div className="contractentity-profile__profile-tabs" role="tablist">
            <QESButton
              id="ce-profile__tab-employed-practitioners"
              role="tab"
              aria-selected={activeProfileTab === "practitioners"}
              aria-controls="ce-profile__tabpanel"
              onClick={() => setActiveProfileTab("practitioners")}
              qesType={activeProfileTab === "practitioners" ? "primary" : "outline"}
              qesTestId="ce-profile__tab-employed-practitioners">
              Employed Practitioners
            </QESButton>
            <QESButton
              id="ce-profile__tab-facilities"
              role="tab"
              aria-selected={activeProfileTab === "facilities"}
              aria-controls="ce-profile__tabpanel"
              onClick={() => setActiveProfileTab("facilities")}
              qesType={activeProfileTab === "facilities" ? "primary" : "outline"}
              qesTestId="ce-profile__tab-facilities">
              Facilities
            </QESButton>
          </div>
        )}
        <div className="contractentity-profile__filters-row">
          <ContractEntityFilters
            activeServiceAreaStatusFilters={activeServiceAreaStatusFilters}
            handleResetAll={handleResetAll}
            handleSearchClick={handleSearchClick}
            filterProviderHubState={providerHubContext.filterState}
            setProviderHubFilter={providerHubContext.setFilter}
            filterState={filterState}
            setFilter={setFilter}
            serviceAreaMatchFilter={serviceAreaMatchFilter}
            serviceAreaStatusFilters={serviceAreaStatusFilters}
            specialtyOptions={specialtyOptions}
            setSpecialtyOptions={setSpecialtyOptions}
            serviceAreaOptions={serviceAreaOptions}
            setServiceAreaOptions={setServiceAreaOptions}
            setActiveServiceAreaStatusFilters={setActiveServiceAreaStatusFilters}
            selectedProviderNetworkTypes={selectedProviderNetworkTypes}
            setSelectedProviderNetworkTypes={setSelectedProviderNetworkTypes}
            providerNetworkTypeOptions={providerNetworkTypeOptions}
          >
            <QESSelect
              containerClassName="item-column contractentity-profile__view-by-column"
              label="View by"
              testId="provider-hub__filter-view-by"
              value={selectedViewByKey}
              onChange={(e) => handleViewByChange(e.target.value)}
            >
              {viewByOptions.map((option) => (
                <option key={option.key} value={option.key}>{option.name}</option>
              ))}
            </QESSelect>
          </ContractEntityFilters>
        </div>

        {/* First Column */}
        <div className="contractentity-profile__grid-column--scroll contractentity-profile__accordions-column">
          {featureFlagContext.enabledFeatureFlags.has(Features.AI_VIRTUAL_ASSISTANT) ? (
            <div className="contractentity-profile__cards-row">
              <ContractEntityProfileKeyPoints />
            </div>
          ) : (<ContractEntityProfileKeyPoints />)}
          <AccordionContainer>
            <ContractEntityProfileOverview />
          </AccordionContainer>
          {!featureFlagContext.enabledFeatureFlags.has(Features.HIDE_PROVIDER_HUB_TIN_ACCORDION) &&
          <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
            <AccordionContainer>
              <ProfileInformationPanel<ProviderHubTinModel>
                title={"Taxpayer Identification Numbers (TINs)"}
                dataName={"TINs"}
                dataValue={"TIN Name"}
                items={selectedContractEntity.tins}
                renderItem={(tin) => (
                  <>
                    <dt className="provider-profile__overview-label">{tin.id}</dt>
                    <dd className="provider-profile__overview-value">
                      {tin.name}
                    </dd>
                  </>
                )} />
            </AccordionContainer>
          </RoleRestricted>
          }
          <AccordionContainer>
            <ContractEntityProfileProviders />
          </AccordionContainer>
          <AccordionContainer>
            <ProviderHubSpecialty
              specialties={selectedContractEntity.specialtyCount}
            />
          </AccordionContainer>
          {!featureFlagContext.enabledFeatureFlags.has(Features.HIDE_PROVIDER_HUB_FACILITY_ACCORDION) &&
            <AccordionContainer>
              <ProfileInformationPanel<ProviderHubFacilityModel>
                title={"Facilities"}
                dataName={"Facilities"}
                dataValue={"Address"}
                items={selectedContractEntity.facilities}
                renderItem={(facility) => (
                  <>
                    <dt className="provider-profile__overview-label">{facility.name}</dt>
                    <dd className="provider-profile__overview-value">
                      {facility.address}
                    </dd>
                  </>
                )} />
            </AccordionContainer>
          }
          {projectContext.project.showCareJourneyScores &&
            <AccordionContainer>
              <ContractEntityProfileCareJourney />
            </AccordionContainer>
          }
        </div>
        {/* Second Column — the tab panel; labelled by whichever tab is active. */}
        <div
          className="contractentity-profile__grid-column--scroll contractentity-profile__table-column"
          ref={rightColumnRef}
          id="ce-profile__tabpanel"
          role="tabpanel"
          aria-labelledby={activeProfileTab === "practitioners" ? "ce-profile__tab-employed-practitioners" : "ce-profile__tab-facilities"}
        >
          {activeProfileTab === "practitioners" ? (
            <>
              {practitionersView === "organization" ? (
                // View by Organization (default): type-1 practitioners grouped under their organization, expandable.
                <ContractEntityEmployedPractitionersTable
                  practitioners={employedPractitionersData}
                  showPci={projectContext.project.showPciData !== false}
                  showVaClaims={projectContext.project.showVaClaimsVolumeColumn !== false}
                  showTricareClaims={projectContext.project.showTricareClaimsVolumeColumn !== false}
                  showMotive={projectContext.project.showMotiveAppropriatenessScore}
                  showCareJourney={projectContext.project.showCareJourneyScores}
                  onOrganizationClick={handleOrganizationClick}
                  onProviderClick={handlePractitionerClick}
                  loading={employedPractitionersLoading}
                />
              ) : (
                // View by Providers: the existing flat provider table.
                <ContractEntityProfileTable
                  contractEntityProviderTableData={contractEntityProviderTableData}
                  loading={providersLoading}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  resetToFirstPage={resetToFirstPage}
                  setContractEntityProviderTableData={setContractEntityProviderTableData}
                  setPageIndex={setPageIndex}
                  setPageSize={setPageSize}
                  setResetToFirstPage={setResetToFirstPage}
                />
              )}
            </>
          ) : (
            <>
              <ContractEntityFacilitiesTable
                viewBy={facilitiesView}
                showPci={projectContext.project.showPciData !== false}
                showMotive={projectContext.project.showMotiveAppropriatenessScore}
                showCareJourney={projectContext.project.showCareJourneyScores}
                onCcnClick={handleCcnClick}
                onOrganizationClick={handleOrganizationClick}
                facilities={facilitiesData}
                ccnGroups={facilityCcnGroups.items}
                useGapsByRegionStatus={facilitiesView === "ccn" ? facilityCcnGroups.useGapsByRegionStatus === true : facilitiesUseGapsByRegionStatus}
                childrenByCcn={facilityChildrenByCcn}
                onExpandCcn={handleExpandCcn}
                ccnPaginationProps={facilityCcnPaginationProps}
                loading={facilitiesView === "ccn" ? facilityCcnGroupsLoading : facilitiesLoading}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}